import React, { useState, useEffect, useRef, useContext } from "react";
import { FaBookmark, FaHeart, FaCog, FaUser, FaTimes, FaBars, FaSignOutAlt, FaBook, FaGoogle, FaLinkedin } from "react-icons/fa";
import SavedJobs from "../../components/SavedJobs/SavedJobs";
import UserPreferences from "../../components/UserPreferences/UserPreferences";
import RecommendedJobs from "../../components/RecommendedJobs/RecommendedJobs";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import googleLogo from "../../assets/google.png";
import linkedinLogo from "../../assets/linkedin.png";
import phdscanner from "../../assets/phdscanner_logo.webp";
import useAnalytics from '../../hooks/useAnalytics';

// Configuration for section titles and sidebar items
const sectionTitles = {
  "saved-jobs": "Saved PhD Opportunities",
  "recommended-jobs": "Recommended PhD Opportunities",
  "user-preferences": "User Preferences"
};

const menuItems = [
  {
    id: 'saved-jobs',
    label: 'Saved Opportunities',
    icon: FaBookmark,
    description: 'View your saved opportunities',
    sectionKey: "saved-jobs"
  },
  {
    id: 'recommended-jobs',
    label: 'Recommended Jobs',
    icon: FaHeart,
    description: 'Opportunities based on your preference',
    sectionKey: "recommended-jobs"
  },
  {
    id: 'user-preferences',
    label: 'User Preferences',
    icon: FaCog,
    description: 'Set your viewing preferences',
    sectionKey: "user-preferences"
  }
];

const SmartPicks = () => {
  const { userDetails, isUserDetailsLoading, handleLogout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [selectedSection, setSelectedSection] = useState("saved-jobs");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const sidebarRef = useRef(null);
  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';
  const { trackEvent } = useAnalytics(GA_MEASUREMENT_ID);

  // Check if user is authenticated
  const isAuthenticated = userDetails && !userDetails.error;

  // Close sidebar on outside click (only in mobile view)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isSidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        !event.target.closest(".menu-button")
      ) {
        setIsSidebarOpen(false);
      }
    };

    if (isSidebarOpen) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }

    return () => document.removeEventListener("click", handleClickOutside);
  }, [isSidebarOpen]);

  const handleGoogleSignIn = () => {
    trackEvent({
      category: 'Button',
      action: 'Click',
      label: 'Google Sign In'
    });
    const googleSignInURL = `${process.env.REACT_APP_API_URL}/api/login`;
    window.location.href = googleSignInURL;
  };

  const handleLinkedInSignIn = () => {
    trackEvent({
      category: 'Button',
      action: 'Click',
      label: 'LinkedIn Sign In'
    });
    const linkedInSignInURL = `${process.env.REACT_APP_API_URL}/api/login/linkedin`;
    window.location.href = linkedInSignInURL;
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (userDetails?.first_name && userDetails?.last_name) {
      return `${userDetails.first_name} ${userDetails.last_name}`;
    } else if (userDetails?.email) {
      return userDetails.email.split('@')[0];
    }
    return 'User';
  };

  // Get user initials
  const getUserInitials = () => {
    if (userDetails?.first_name && userDetails?.last_name) {
      return `${userDetails.first_name[0]}${userDetails.last_name[0]}`.toUpperCase();
    } else if (userDetails?.email) {
      return userDetails.email[0].toUpperCase();
    }
    return 'U';
  };

  // Show loading spinner while checking authentication
  if (isUserDetailsLoading) {
    return (
      <div className="min-h-screen bg-[#FFEEED] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#DB4F40] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#1B222A]">Loading Smart Picks...</p>
        </div>
      </div>
    );
  }

  const SidebarContent = () => (
    <div className="h-full flex flex-col">
      {/* User Info Header */}
      <div className="p-6 border-b border-[#FFD6D4] bg-[#FFEEED]/50">
        <div className="flex items-center">
          <div className="w-16 h-16 rounded-lg flex items-center justify-center mr-4 overflow-hidden bg-white shadow-sm">
            <div className="w-12 h-12 bg-gradient-to-r from-[#DB4F40] to-[#c13426] rounded-full flex items-center justify-center">
              <span className="text-white font-medium text-lg">
                {isAuthenticated ? getUserInitials() : '?'}
              </span>
            </div>
          </div>
          <div>
            <h3 className="font-semibold text-[#1B222A]">
              {isAuthenticated ? getUserDisplayName() : 'Guest User'}
            </h3>
            <p className="text-sm text-[#1B222A]/70 italic">Smart Picks Dashboard</p>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 p-4 bg-white/80 overflow-y-auto">
        <div className="space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setSelectedSection(item.sectionKey);
                  setIsSidebarOpen(false);
                }}
                className={`w-full flex items-center p-3 rounded-xl transition-all duration-200 group ${
                  selectedSection === item.sectionKey
                    ? 'bg-[#FFD6D4] text-[#DB4F40] border border-[#DB4F40]/20 shadow-md'
                    : 'text-[#1B222A] hover:bg-[#FFEEED] hover:text-[#DB4F40]'
                }`}
              >
                <Icon className={`w-5 h-5 mr-3 ${
                  selectedSection === item.sectionKey ? 'text-[#DB4F40]' : 'text-[#1B222A]/60 group-hover:text-[#DB4F40]'
                }`} />
                <div className="text-left">
                  <div className="font-medium">{item.label}</div>
                  <div className="text-xs text-[#1B222A]/70">{item.description}</div>
                </div>
              </button>
            );
          })}
        </div>
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-[#FFD6D4] bg-white/90 backdrop-blur-sm flex-shrink-0">
        {isAuthenticated ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-[#DB4F40] to-[#c13426] rounded-full flex items-center justify-center mr-3 shadow-md">
                <span className="text-white font-medium">
                  {getUserInitials()}
                </span>
              </div>
              <div>
                <p className="font-medium text-[#1B222A] truncate">{getUserDisplayName()}</p>
                <p className="text-xs text-[#1B222A]/70 truncate">{userDetails?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="text-[#1B222A]/60 hover:text-[#DB4F40] transition-colors"
              title="Logout"
            >
              <FaSignOutAlt className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="text-center">
            <p className="text-sm text-[#1B222A]/70 mb-2">Sign in to access all features</p>
            <button
              onClick={() => setSelectedSection("login")}
              className="w-full px-4 py-2 bg-[#DB4F40] text-white rounded-lg hover:bg-[#c13426] transition-colors text-sm font-medium"
            >
              Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const LoginContent = () => (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white p-4">
      <div className="bg-white shadow-lg rounded-lg p-8 md:p-12 w-full max-w-lg">
        <div className="flex justify-center mb-8">
          <img
            src={phdscanner}
            alt="Logo"
            className="h-14 w-auto object-contain mr-3"
          />
        </div>

        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-[#1B222A] mb-2">Welcome to Smart Picks</h2>
          <p className="text-gray-600">Sign in to access your personalized dashboard</p>
        </div>

        <div className="space-y-4">
          <button
            onClick={handleGoogleSignIn}
            className="flex items-center justify-center w-full px-4 py-3 border border-gray-300 rounded-md shadow-md bg-white text-gray-700 hover:bg-gray-50 transition duration-300 transform hover:scale-105"
          >
            <img src={googleLogo} alt="Google Logo" className="h-6 w-6 mr-3" />
            <span className="text-lg font-medium">Sign in with Google</span>
          </button>

          <button
            onClick={handleLinkedInSignIn}
            className="flex items-center justify-center w-full px-4 py-3 border border-gray-300 rounded-md shadow-md bg-white text-gray-700 hover:bg-gray-50 transition duration-300 transform hover:scale-105"
          >
            <img src={linkedinLogo} alt="LinkedIn Logo" className="h-6 w-6 mr-3" />
            <span className="text-lg font-medium">Sign in with LinkedIn</span>
          </button>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            By signing in, you'll be able to:
          </p>
          <ul className="text-sm text-gray-600 mt-2 space-y-1">
            <li>• Save and manage your favorite opportunities</li>
            <li>• Get personalized job recommendations</li>
            <li>• Set your preferences for better matches</li>
            <li>• Track your application progress</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    if (!isAuthenticated) {
      return <LoginContent />;
    }

    switch (selectedSection) {
      case "saved-jobs":
        return <SavedJobs />;
      case "recommended-jobs":
        return <RecommendedJobs setSelectedSection={setSelectedSection} />;
      case "user-preferences":
        return <UserPreferences />;
      default:
        return <SavedJobs />;
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Floating background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#FFD6D4] rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse"></div>
        <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-[#FFEEED] rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-3/4 w-80 h-80 bg-[#FFD6D4] rounded-full mix-blend-multiply filter blur-xl opacity-25 animate-pulse delay-500"></div>
      </div>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        ref={sidebarRef}
        className={`lg:hidden fixed top-20 left-0 w-80 bg-white border-r border-[#FFD6D4] 
                  shadow-xl z-40 transform transition-transform duration-300 ease-out
                  ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ 
          height: 'calc(100vh - 5rem)',
          maxHeight: 'calc(100vh - 5rem)'
        }}
      >
        <SidebarContent />
      </aside>

      {/* Desktop Layout */}
      <div className="hidden lg:flex relative">
        {/* Desktop Sidebar */}
        <aside className="sticky top-20 w-80 bg-white border-r border-[#FFD6D4] 
                        flex-shrink-0 h-[calc(100vh-5rem)] overflow-y-auto">
          <SidebarContent />
        </aside>

        {/* Desktop Main Content Area */}
        <main className="flex-1 bg-white">
          {isAuthenticated && (
            <>
              {/* Section Title */}
              <div className="bg-white border-b border-[#FFD6D4] shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gradient-to-br from-[#DB4F40] to-[#C64537] rounded-lg">
                      <FaBookmark className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                        {sectionTitles[selectedSection] || 'Smart Picks'}
                      </h2>
                      <p className="text-xs text-gray-600 mt-1">
                        {selectedSection === "saved-jobs" ? "View your saved opportunities" :
                         selectedSection === "recommended-jobs" ? "Opportunities based on your preference" :
                         "Set your viewing preferences"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 sm:p-6 pb-12 w-full">
                <div className="relative z-10">
                  {renderContent()}
                </div>
              </div>
            </>
          )}

          {!isAuthenticated && (
            <div className="flex-1">
              {renderContent()}
            </div>
          )}
        </main>
      </div>

      {/* Mobile Main Content Area */}
      <main className="lg:hidden bg-white w-full">
        {isAuthenticated && (
          <>
            {/* Section Title */}
            <div className="bg-white border-b border-[#FFD6D4] shadow-sm">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gradient-to-br from-[#DB4F40] to-[#C64537] rounded-lg">
                      <FaBookmark className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                        {sectionTitles[selectedSection] || 'Smart Picks'}
                      </h2>
                      <p className="text-xs text-gray-600 mt-1">
                        {selectedSection === "saved-jobs" ? "View your saved opportunities" :
                         selectedSection === "recommended-jobs" ? "Opportunities based on your preference" :
                         "Set your viewing preferences"}
                      </p>
                    </div>
                  </div>

                  {/* Mobile Menu Toggle */}
                  <button
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                    className="lg:hidden inline-flex items-center px-3 py-2 bg-[#DB4F40] text-white rounded-lg hover:bg-[#C64537] transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <FaBars className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">Menu</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 pb-12 w-full">
              <div className="relative z-10">
                {renderContent()}
              </div>
            </div>
          </>
        )}

        {!isAuthenticated && (
          <>
            {/* Mobile Header with Menu Toggle */}
            <div className="bg-white border-b border-[#FFD6D4] shadow-sm">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gradient-to-br from-[#DB4F40] to-[#C64537] rounded-lg">
                      <FaBookmark className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                        Smart Picks
                      </h2>
                      <p className="text-xs text-gray-600 mt-1">
                        Sign in to access your dashboard
                      </p>
                    </div>
                  </div>

                  {/* Mobile Menu Toggle */}
                  <button
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                    className="lg:hidden inline-flex items-center px-3 py-2 bg-[#DB4F40] text-white rounded-lg hover:bg-[#C64537] transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <FaBars className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">Menu</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 pb-12 w-full">
              <div className="relative z-10">
                {renderContent()}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default SmartPicks;
